import { useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { useAppSelector } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import RiskTypeChip from "@/features/risks/entities/risk/ui/RiskTypeChip"
import UnitRiskHeatmap from "./UnitRiskHeatmap"
import { Unit } from "../model/unit.types"

interface Props {
  unit: Unit
  onClick?: () => void
}

export default function UnitListRow({ unit, onClick }: Props) {
  const risks = useAppSelector(riskStore.selectors.list)

  const { rpsCount, phyCount, rpsColor, phyColor, rpsItems, phyItems } = useMemo(() => {
    const validated = risks.filter((r) => r.UnitId === unit.id && r.dateValidated)

    const rps = validated.filter((r) => r.rps).sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))
    const phy = validated.filter((r) => !r.rps).sort((a, b) => (b.valueEffective ?? 0) - (a.valueEffective ?? 0))

    const worstRps = rps.length > 0 ? rps[0] : null
    const worstPhy = phy.length > 0 ? phy[0] : null

    const rpsHeatmap = rps.map((risk) => ({
      color: risk.valueEffectiveColor,
      title: `${risk.valueEffectiveLabel} • ${risk.name ?? ""}`
    }))

    const phyHeatmap = phy.map((risk) => ({
      color: risk.valueEffectiveColor,
      title: `${risk.valueEffectiveLabel} • ${risk.name ?? ""}`
    }))

    return {
      rpsCount: rps.length,
      phyCount: phy.length,
      rpsColor: worstRps?.valueEffectiveColor,
      phyColor: worstPhy?.valueEffectiveColor,
      rpsItems: rpsHeatmap,
      phyItems: phyHeatmap
    }
  }, [risks, unit.id])

  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 180px 180px 24px",
        gap: "1rem",
        alignItems: "center",
        padding: "1rem 1.5rem"
      }}
      className="border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-colors shadow-sm">
      <div className="flex items-center gap-4 min-w-0">
        <h3 className="font-semibold text-base truncate">{unit.name}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500 flex-shrink-0">
          <FontAwesomeIcon icon={faUser} size="xs" />
          {unit.size != null ? unit.size : "NC"}
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end">
        {phyCount > 0 ? (
          <>
            <div className="min-w-[70px]">
              <UnitRiskHeatmap items={phyItems} size={8} gap={2} cols={8} />
            </div>
            <RiskTypeChip
              isRps={false}
              count={phyCount}
              showCount
              color={phyColor || undefined}
              isSmall
              showLabel={true}
            />
          </>
        ) : (
          <RiskTypeChip isRps={false} count={0} showCount color="#ccc" isSmall showLabel={true} />
        )}
      </div>

      <div className="flex items-center gap-2 justify-end">
        {rpsCount > 0 ? (
          <>
            <div className="min-w-[70px]">
              <UnitRiskHeatmap items={rpsItems} size={8} gap={2} cols={8} />
            </div>
            <RiskTypeChip
              isRps={true}
              count={rpsCount}
              showCount
              color={rpsColor || undefined}
              isSmall
              showLabel={true}
            />
          </>
        ) : (
          <RiskTypeChip isRps={true} count={0} showCount color="#ccc" isSmall showLabel={true} />
        )}
      </div>

      <div className="flex justify-center">
        <FontAwesomeIcon icon={faChevronRight} className="text-gray-400" />
      </div>
    </div>
  )
}
